import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiMail, FiArrowRight, FiArrowLeft, FiShield, FiCheck } from "react-icons/fi";
import { GoPasskeyFill } from "react-icons/go";
import { IoMdUnlock } from "react-icons/io";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";

import { NGROK } from "../../../APIs";
import UserService, { useKeycloakStore } from "../../../services/UserService";
import { myLocalStorage } from "../../../components/StorageHelper";
import { decodeKeycloakToken } from "../../../utils/tokenHelpers";
import useUserStore from "../../../services/userStore";
import useAuth from "../../../hooks/useAuth";
import AuthenticationPage from "./components/AuthenticationPage";

const AuthSignUp = () => {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { getUserData: fetchUserData } = useUserStore((state) => state);
  const { local_kc } = useKeycloakStore();

  // Step states: 1=email, 2=OTP, 3=signup options, 4=tenant name
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [tenantError, setTenantError] = useState("");
  const [serviceTermsAgreed, setServiceTermsAgreed] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isTenantNameFormActive =
    local_kc &&
    (local_kc.realm === "whiteswan_tenants" ||
      local_kc.realm === "whiteswan_tenants_legacy");

  // Validation schemas
  const SignupSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email format")
      .trim("No whitespace allowed")
      .strict(true)
      .required("Email is required"),
    tenantName: Yup.string()
      .min(5, "Too Short!")
      .max(50, "Too Long!")
      .required("Required")
      .test(
        "is-lowercase",
        "Only lowercase letters are allowed.",
        (value) => !/[A-Z]/.test(value)
      ),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      tenantName: "",
    },
    validationSchema: SignupSchema,
    onSubmit: () => {},
  });

  // API methods
  const getTenantsInfo = async (email) => {
    const axiosInstance = axios.create();
    try {
      const tenants = await axiosInstance.get(
        `${NGROK}/api/get-tenants?email=${email.toLowerCase().trim()}`
      );
      return tenants;
    } catch (error) {
      setTenantName("");
      return null;
    }
  };

  const checkIfUserExist = async (email) => {
    const tenants = await getTenantsInfo(email.toLowerCase().trim());

    if (!tenants || !tenants.data?.length) {
      setTenantName("");
      return false;
    } else if (tenants?.data?.length) {
      myLocalStorage.setItem("role", tenants?.data[0].role);
      setFirstName(tenants?.data[0].firstName);
      setLastName(tenants?.data[0].lastName);
      setTenantName(tenants?.data[0].tenantName);
      return tenants?.data[0];
    }
  };

  const keycloakInitAndRegister = async (realm) => {
    try {
      setLoading(true);
      myLocalStorage.setItem("realm", realm);
      UserService.initKeycloak(realm);

      setTimeout(() => {
        UserService.doRegister(email.toLowerCase().trim(), firstName, lastName);
      }, 2000);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const verifyEmailInvite = useCallback(
    async (otp) => {
      const axiosInstance = axios.create();
      let params = {
        email: email.toLowerCase().trim(),
        verificationCode: parseInt(otp),
      };

      try {
        let { data } = await axiosInstance.post(
          `${NGROK}/api/invite/validate`,
          params
        );
        if (!data.isOk) {
          setOtpError("Invalid or expired code. Please try again.");
          return false;
        } else {
          setOtpError("");
          return true;
        }
      } catch (error) {
        console.error(error);
        setOtpError("Unable to verify code. Please try again.");
        return false;
      }
    },
    [email]
  );

  // Handlers
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setSubmitLoading(true);
    setEmailError("");

    const errors = await formik.validateForm();
    if (!errors?.email) {
      formik.handleSubmit();
      const user = await checkIfUserExist(email.toLowerCase().trim());

      if (!user) {
        // New user - go to signup options
        setStep(3);
      } else {
        if (
          user?.realm?.keycloakAccountExists ||
          user?.legacyRealm?.keycloakAccountExists
        ) {
          setEmailError("User with this email already exists.");
          setSubmitLoading(false);
          return;
        } else if (user.isVerificationRequired) {
          // Need OTP verification
          setStep(2);
        } else {
          // Go to signup options
          setStep(3);
        }
      }
      setSubmitLoading(false);
    } else {
      setEmailError(errors.email);
      setSubmitLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setOtpError("");

    const isValid = await verifyEmailInvite(otp);
    if (isValid) {
      setStep(3);
    }
    setSubmitLoading(false);
  };

  const startRegisterViaPasskey = async () => {
    if (!serviceTermsAgreed) return;
    
    setLoading(true);
    const user = await checkIfUserExist(email.toLowerCase().trim());

    if (
      user?.legacyRealm?.realmName === "whiteswan_tenants_initial" ||
      user?.realm?.keycloakAccountExists
    ) {
      setEmailError("User with this email already exists.");
      setLoading(false);
      return;
    }

    if (!user) {
      myLocalStorage.setItem("role", "TENANT_ADMIN");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("LastName", lastName);
      keycloakInitAndRegister("whiteswan_tenants");
    } else if (
      user &&
      user.isInvitedAdmin &&
      !user.realm.keycloakAccountExists
    ) {
      myLocalStorage.setItem("role", "TENANT_ADMIN");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("LastName", lastName);
      keycloakInitAndRegister("whiteswan_tenants");
    } else if (user && !user.realm.keycloakAccountExists) {
      myLocalStorage.setItem("role", "TENANT_USER");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("LastName", lastName);
      keycloakInitAndRegister(user.tenantName);
    }
  };

  const startRegisterViaPassword = async () => {
    if (!serviceTermsAgreed) return;
    
    setLoading(true);
    const user = await checkIfUserExist(email.toLowerCase().trim());

    if (
      user?.legacyRealm?.realmName === "whiteswan_tenants_initial" ||
      user?.legacyRealm?.keycloakAccountExists
    ) {
      setEmailError("User with this email already exists.");
      setLoading(false);
      return;
    }

    if (!user) {
      myLocalStorage.setItem("role", "TENANT_ADMIN");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("LastName", lastName);
      keycloakInitAndRegister("whiteswan_tenants_legacy");
    } else if (
      user &&
      user.isInvitedAdmin &&
      !user.realm.keycloakAccountExists
    ) {
      myLocalStorage.setItem("role", "TENANT_ADMIN");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("LastName", lastName);
      keycloakInitAndRegister("whiteswan_tenants_legacy");
    } else if (
      user &&
      !user.isInvitedAdmin &&
      !user.legacyRealm.keycloakAccountExists
    ) {
      myLocalStorage.setItem("role", "TENANT_USER");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("lastName", lastName);
      keycloakInitAndRegister(`${user.legacyRealm.realmName}`);
    }
  };

  const createTenant = async () => {
    setIsSubmitted(true);
    const errors = await formik.validateForm();
    
    if (errors?.tenantName) {
      setTenantError(errors.tenantName);
      return;
    }

    setLoading(true);

    const { data: tenantAlreadyExist } = await axios.get(
      `${NGROK}/api/test-connection?tenantName=${formik.values.tenantName}`
    );

    if (!tenantAlreadyExist && formik.values.tenantName) {
      const {
        email: userEmail,
        family_name,
        given_name,
      } = decodeKeycloakToken(local_kc.token);

      try {
        const response = await signUp(
          formik.values.tenantName,
          userEmail,
          `${family_name} ${given_name}`
        );
        fetchUserData(userEmail);

        if (response) {
          navigate("/loading");
        } else {
          setLoading(false);
        }
      } catch (error) {
        setTenantError("Something went wrong during test connection.");
        console.error(error, "sign up error");
        setLoading(false);
      }
    }
  };

  const stringToColor = (name) => {
    let hash = 0;
    let i;

    for (i = 0; i < name.length; i += 1) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
  };

  const getInitials = (name) => {
    let initials = "";
    const nameParts = name.split(" ");

    if (nameParts.length >= 2) {
      initials = `${nameParts[0][0]}${nameParts[1][0]}`;
    } else if (name.includes("@")) {
      const emailParts = name.split("@")[0].split(".");
      if (emailParts.length >= 2) {
        initials = `${emailParts[0][0]}${emailParts[1][0]}`;
      } else {
        initials = `${name[0]}${name[1]}`;
      }
    } else {
      initials = `${name[0]}${name[1]}`;
    }

    return initials.toUpperCase();
  };

  const realm = myLocalStorage.getItem("realm");
  const access_token =
    UserService?.getToken() || myLocalStorage.getItem("access_token");

  useEffect(() => {
    if (!access_token && realm) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 4000);
    }
  }, [access_token, realm]);

  // Loading overlay
  if (loading) {
    return (
      <AuthenticationPage>
        <div className="fixed inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-lg font-semibold text-indigo-600">
              Navigating to Signup page
            </p>
          </div>
        </div>
      </AuthenticationPage>
    );
  }

  return (
    <AuthenticationPage>
      <div className="w-full max-w-md px-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden animate-fadeIn">
          <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-600"></div>

          <div className="p-6 flex flex-col min-h-[500px]">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-lg mb-3">
                <FiShield className="text-white text-2xl" />
              </div>
              <h1 className="text-2xl font-bold">
                {isTenantNameFormActive ? "Setup Tenant Name" : "Create Account"}
              </h1>
              <p className="text-sm text-gray-600">
                {step === 1 && "Enter your email to get started"}
                {step === 2 && "Verify your email"}
                {step === 3 && "Choose your signup method"}
                {isTenantNameFormActive && "Configure your workspace"}
              </p>
            </div>

            {/* Step 1: Email */}
            {step === 1 && !isTenantNameFormActive && (
              <form onSubmit={handleEmailSubmit} className="flex-1 flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiMail className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value.toLowerCase().trim());
                        formik.handleChange(e);
                        setEmailError("");
                        setIsSubmitted(false);
                      }}
                      placeholder="you@example.com"
                      className="block w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                      required
                    />
                    <button
                      type="submit"
                      disabled={!email || submitLoading}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <div className="bg-indigo-100 hover:bg-indigo-200 rounded-lg p-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                        {submitLoading ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                        ) : (
                          <FiArrowRight className="text-indigo-600 text-lg" />
                        )}
                      </div>
                    </button>
                  </div>
                  {emailError && (
                    <div className="mt-2 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm animate-slideDown">
                      {emailError}
                    </div>
                  )}
                </div>
              </form>
            )}

            {/* Step 2: OTP Verification */}
            {step === 2 && (
              <form onSubmit={handleOtpSubmit} className="flex-1 flex flex-col gap-4">
                <div className="flex items-center gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-all"
                  >
                    <FiArrowLeft className="text-gray-600" />
                  </button>
                  <span className="text-xs text-gray-600">{email}</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value);
                      setOtpError("");
                    }}
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    className="block w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-center text-lg tracking-widest"
                    required
                  />
                  {otpError && (
                    <div className="mt-2 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm animate-slideDown">
                      {otpError}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={!otp || submitLoading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-xl font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitLoading ? "Verifying..." : "Verify Code"}
                </button>
              </form>
            )}

            {/* Step 3: Signup Options */}
            {step === 3 && !isTenantNameFormActive && (
              <div className="flex-1 flex flex-col gap-4">
                <div className="flex flex-col items-center gap-3 mb-4">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg"
                    style={{ backgroundColor: stringToColor(email) }}
                  >
                    {getInitials(email)}
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold text-gray-800">
                      Hello, {firstName} {lastName}
                    </p>
                    <p className="text-sm text-gray-600">{email}</p>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={startRegisterViaPasskey}
                    disabled={!serviceTermsAgreed || loading}
                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <GoPasskeyFill className="text-xl" />
                    Sign up with Passkey
                  </button>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-300"></div>
                    <span className="text-xs text-gray-500 font-medium">OR</span>
                    <div className="flex-1 h-px bg-gray-300"></div>
                  </div>

                  <button
                    onClick={startRegisterViaPassword}
                    disabled={!serviceTermsAgreed || loading}
                    className="w-full flex items-center justify-center gap-2 border-2 border-indigo-200 text-indigo-700 py-3 rounded-xl font-semibold hover:border-indigo-500 hover:bg-indigo-50 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <IoMdUnlock className="text-xl" />
                    Sign up with Password & OTP
                  </button>
                </div>

                <div className="mt-4">
                  <label className="flex items-start gap-2 cursor-pointer group">
                    <div className="relative flex items-center justify-center mt-0.5">
                      <input
                        type="checkbox"
                        checked={serviceTermsAgreed}
                        onChange={(e) => setServiceTermsAgreed(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 border-2 rounded transition-all ${
                        serviceTermsAgreed 
                          ? 'bg-indigo-600 border-indigo-600' 
                          : 'border-gray-300 group-hover:border-indigo-400'
                      }`}>
                        {serviceTermsAgreed && (
                          <FiCheck className="text-white text-sm" strokeWidth={3} />
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-gray-700">
                      I agree to the{" "}
                      <a href="/terms_of_service" className="text-indigo-600 hover:underline font-medium">
                        Terms and Conditions
                      </a>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Step 4: Tenant Name (Keycloak callback) */}
            {isTenantNameFormActive && (
              <div className="flex-1 flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tenant Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="tenantName"
                      value={formik.values.tenantName}
                      onChange={(e) => {
                        formik.handleChange(e);
                        setTenantError("");
                        setIsSubmitted(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          createTenant();
                        }
                      }}
                      placeholder="Enter tenant name"
                      className="block w-full px-4 pr-12 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all lowercase"
                      required
                    />
                    <button
                      type="button"
                      onClick={createTenant}
                      disabled={!formik.values.tenantName || submitLoading}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <div className="bg-indigo-100 hover:bg-indigo-200 rounded-lg p-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                        {submitLoading ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                        ) : (
                          <FiArrowRight className="text-indigo-600 text-lg" />
                        )}
                      </div>
                    </button>
                  </div>
                  {tenantError && isSubmitted && (
                    <div className="mt-2 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm animate-slideDown">
                      {tenantError}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-gray-500">
                    Must be 5-50 characters, lowercase letters only
                  </p>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-6 text-center text-sm text-gray-600">
              <p>
                Already have an account?{" "}
                <button
                  onClick={() => navigate("/signIn")}
                  className="text-indigo-600 font-semibold hover:underline"
                >
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuthenticationPage>
  );
};

export default AuthSignUp;
